import { Table, Tag, Space, message } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { UseMutateAsyncFunction, useMutation } from "react-query";
import { deleteCurrency, getCurrencyById } from "../../../api/currency";
import { AddEditCurrency } from "./AddEditCurrency";
import { CurrencyType } from "./currencyType";
import EditButton from "../../UI/EditButton/editbutton";
import DeleteButton from "../../UI/DeleteButton/deletebutton";
import DeleteModal from "../../UI/ModalComponent/DeleteModal/deletemodal";
import { AxiosResponse } from "axios";

interface inventoryProps {
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  filter: boolean | undefined;
  search: string;
  paginatedCurrency: any;
  isCurrencyLoading: boolean;
  setPaginatedCurrency: Dispatch<any>;
  CurrencyMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      limit: number;
      q: string;
      isActive?: boolean | undefined;
    },
    unknown
  >;
  tableKey: string;
}
interface CurrencyInterface {
  id: number;
  name: string;
  abbreviation: string;
  symbol: string;
  isActive: boolean;
}

const CurrencyList = ({
  paginatedCurrency,
  isCurrencyLoading,
  page,
  setPage,
  filter,
  search,
  setPaginatedCurrency,
  CurrencyMutation,
  tableKey,
}: inventoryProps) => {
  const [t] = useTranslation("common");
  const [openCurrency, setOpenCurrency] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<any>();
  const [Currency, setCurrency] = useState<CurrencyType>();
  const [prevPage, setPrevPage] = useState<number>(0);
  const [maxPage, setMaxPage] = useState<number>(0);
  const getCurrencyMutation = useMutation("getCurrency", getCurrencyById);
  const [, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);
  const [nameToDelete, setNameToDelete] = useState<any>();

  useEffect(() => {
    page > prevPage &&
      maxPage < page &&
      CurrencyMutation({
        limit: page * 8 + 8,
        q: search,
        isActive: filter,
      }).then((res) => {
        if (page > maxPage) setMaxPage(page);
        setPaginatedCurrency(res.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  function fillCurrency(id: any) {
    getCurrencyMutation.mutate(id, {
      onSuccess: (res) => {
        setCurrency(res?.data);
        setOpenCurrency(true);
      },
    });
  }

  const onError = (error: any, context: unknown) => {
    if (setError) {
      setError(error.response.data.message);
      message.error({
        content: error.response.data.message,
        duration: 10,
        onClick: () => {
          message.destroy();
        },
      });
    }
  };

  const deleteCurrencyMutation = useMutation("deleteCurrency", deleteCurrency, {
    onSuccess: () => {
      CurrencyMutation({
        limit: page * 8 + 8,
        q: search,
        isActive: filter,
      }).then((res) => {
        setPaginatedCurrency(res.data);
        message.success("Complete successfully");
      });
    },
    onError,
  });

  const DeleteCurrency = (id: string | undefined, name: string): void => {
    setOpen(true);
    setIdToDelete(id);
    setNameToDelete(name);
  };

  const handleOk = () => {
    if (idToDelete) {
      deleteCurrencyMutation.mutate({ id: idToDelete });
    }
    setOpen(false);
  };

  const columns: ColumnsType<CurrencyInterface> = [
    {
      title: `${t("currency")}`,
      dataIndex: "name",
      key: "name",
      className: "table-header",
    },
    {
      title: `${t("abbreviation")}`,
      dataIndex: "abbreviation",
      key: "abbreviation",
      width: "8rem",
    },
    {
      title: `${t("symbol")}`,
      dataIndex: "symbol",
      key: "symbol",
      width: "7rem",
    },
    {
      title: `${t("status")}`,
      dataIndex: "isActive",
      key: "isActive",
      width: "5rem",
      align: "center",
      render: (record: any) =>
        record ? (
          <Tag className="active">Active</Tag>
        ) : (
          <Tag className="inactive">Inactive</Tag>
        ),
    },
    {
      title: `${t("action")}`,
      key: "action",
      fixed: "right",
      align: "center",
      width: "5rem",
      render: (record: any) => (
        <Space>
          <EditButton
            onClick={() => {
              setSelectedCurrency(record.id);
              fillCurrency(record.id);
            }}
          />
          <DeleteButton
            onClick={() => DeleteCurrency(record?.id, record?.name)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <DeleteModal
        open={open}
        setOpen={setOpen}
        name={nameToDelete}
        onOk={handleOk}
      />

      {openCurrency && Currency && (
        <AddEditCurrency
          Currency={Currency}
          openCurrency={openCurrency}
          setOpenCurrency={setOpenCurrency}
          id={selectedCurrency}
          setPaginatedCurrency={setPaginatedCurrency}
          CurrencyMutation={CurrencyMutation}
          page={page}
          filter={filter}
          search={search}
        />
      )}

      <Table
        key={tableKey}
        scroll={{ x: 1000 }}
        columns={columns}
        dataSource={paginatedCurrency}
        pagination={{ pageSize: 8 }}
        loading={isCurrencyLoading}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        onChange={(selectedPage) => {
          setPrevPage(page);
          setPage(selectedPage?.current ?? 1);
        }}
        rowKey={(record) => record.id}
      />
    </>
  );
};

export default CurrencyList;
